// src/constants/dimensions.ts
// VARK 四维度的全部展示数据 single source of truth。
// view / component / share-card / scoring 全部从此读取,不要在别处复刻字面量。
import type { Dim } from '../types/index.js';

export const DIMS: readonly Dim[] = ['V', 'A', 'R', 'K'];

export const DIM_NAME: Record<Dim, string> = { V: '视觉', A: '听觉', R: '读写', K: '动觉' };

export const DIM_NAME_EN: Record<Dim, string> = {
  V: 'Visual',
  A: 'Aural',
  R: 'Read/Write',
  K: 'Kinesthetic',
};

export const DIM_FULL: Record<Dim, string> = {
  V: `${DIM_NAME.V} (${DIM_NAME_EN.V})`,
  A: `${DIM_NAME.A} (${DIM_NAME_EN.A})`,
  R: `${DIM_NAME.R} (${DIM_NAME_EN.R})`,
  K: `${DIM_NAME.K} (${DIM_NAME_EN.K})`,
};

export const DIM_EMOJI: Record<Dim, string> = { V: '👁', A: '👂', R: '📖', K: '✋' };

// 用于 plainSummary 双通道句式
export const DIM_VERB_LONG: Record<Dim, string> = {
  V: '看图', A: '听人讲', R: '读文字', K: '动手做',
};
export const DIM_VERB_SHORT: Record<Dim, string> = {
  V: '看', A: '听', R: '读', K: '上手',
};

// 维度的官方释义(用于结果页"你是怎样学习的"专业说明)
export const DIM_FORMAL: Record<Dim, string> = {
  V: '你倾向通过图形、图表、空间布局来吸收信息。比起文字,你更容易记住一张示意图;脑海中常会把内容转换成画面。',
  A: '你擅长通过倾听吸收信息。讲座、讨论、播客对你格外有效;读书时把文字念出声会显著提升理解。',
  R: '你偏好文字形式的信息。把内容写下来、整理成 outline 是你最自然的学习方式;阅读和书面笔记是你的强项。',
  K: '你需要动手实践才能真正理解。比起理论,你更相信经验;实操、案例、模拟是你高效学习的关键。',
};

// 维度的"日常画像"(口语化举例)
export const DIM_EVERYDAY: Record<Dim, string> = {
  V: '别人讲半天的事情,你一张图就懂了。看视频时往往关字幕也能跟上画面。',
  A: '听播客 / 听课 / 听别人讲是你最舒服的状态。脑子里默念时,常会无意识地"听到"自己声音。',
  R: '看到密集的文字不会发怵,反而觉得安心。会下意识做笔记、整理 outline,看到清单就想打勾。',
  K: '坐久了就要起来动一动。学新东西时第一反应是"先上手试试再说"。手做过的事比看过的事记得牢。',
};

// 优势短句
export const DIM_STRENGTH: Record<Dim, string> = {
  V: '空间感与画面记忆',
  A: '语言节奏与听辨理解',
  R: '文字组织与系统总结',
  K: '动手能力与经验直觉',
};

// 盲区
export const DIM_BLINDSPOT: Record<Dim, string> = {
  V: '纯文字材料容易让你走神,要刻意做笔记或画图才能进得去',
  A: '只看不听,难以记住细节;书面 PPT 看完很快就忘',
  R: '面对动态的视频或现场讨论,信息密度太大反而抓不住重点',
  K: '坐着不动接收信息时,你常会觉得"懂了",但事后才发现知识并没真正落地',
};

// 避免这样学
export const DIM_AVOID: Record<Dim, string> = {
  V: '只听不看 — 单纯听 podcast / 讲座对你帮助有限,务必配合视觉材料',
  A: '默读硬记 — 只盯着文字看,效率会比朗读 / 听讲低不少',
  R: '只看图不写字 — 看完图示要把要点写下来才能内化',
  K: '只听 / 只读不动手 — 没有实操环节,理论很快会"看完就忘"',
};

// 三种典型场景下的具体做法
export interface ScenarioGuide {
  newSkill: string;
  exam: string;
  presenting: string;
}

export const DIM_SCENARIO: Record<Dim, ScenarioGuide> = {
  V: {
    newSkill: '找视频教程 + 信息图入门,先在脑海里建画面',
    exam: '把整本书/课程提炼成一张"地图"——思维导图或时间线',
    presenting: '用 PPT 视觉化你的逻辑,每页一图代替密集文字',
  },
  A: {
    newSkill: '找播客 / 公开课跟读,通勤路上反复听',
    exam: '录下自己讲解知识点,睡前 / 出门时回放',
    presenting: '对着空房间或同事讲一遍,声音帮你梳理逻辑',
  },
  R: {
    newSkill: '读官方文档 / 教材,边读边写自己的理解',
    exam: '整理成 outline,把每章浓缩成 200 字摘要',
    presenting: '先写完整逐字稿,再提炼成要点',
  },
  K: {
    newSkill: '找一个最小项目立刻上手,试错中学习',
    exam: '做模拟题 / 真题,带着问题回查知识点',
    presenting: '彩排 2 次,边走边讲,身体记住节奏',
  },
};

// 微习惯
export const DIM_HABIT: Record<Dim, string> = {
  V: '看视频 / 上课时,顺手画一张速记图(5 分钟即可)',
  A: '把今天学的一个知识点,出门前讲给空气听 1 分钟',
  R: '睡前用 100 字写下今天最大的"啊哈时刻"',
  K: '每周找一个新概念,动手做出 / 用出一个小例子',
};

// 首页 4 维度卡片简短介绍
export const DIM_HOME_TAGLINE: Record<Dim, string> = {
  V: '偏好图形、图表、空间布局,把信息转换成画面来理解。',
  A: '通过倾听、讨论、口头讲解吸收信息,听比看更有效。',
  R: '偏好文字阅读与书面表达,把笔记写一遍胜过听十遍。',
  K: '需要动手实践,通过操作、案例、真实经验来理解。',
};
